// TMOS Maintenance Screen Component
// Date: November 15, 2025
// Purpose: Display maintenance mode screen to users

import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import tmosLogo from '@/assets/images/brand/tmos-logo.png';
import { Card, CardContent } from '@/components/ui/Card';
import { useLanguage } from '@/state/language/LanguageProvider';
import { maintenanceModeService } from '@/services/maintenanceModeService';

interface MaintenanceScreenProps {
  message?: string;
  scheduledEndTime?: string;
}

/**
 * MaintenanceScreen Component
 * 
 * Displays a maintenance mode screen with customizable message and countdown timer.
 * Shows time remaining if animated, otherwise shows exact end time.
 * Fetches maintenance status from the API if not provided as props.
 */
export const MaintenanceScreen: React.FC<MaintenanceScreenProps> = ({
  message: propMessage,
  scheduledEndTime: propScheduledEndTime
}) => {
  const { t } = useLanguage();
  const [message, setMessage] = useState<string>(propMessage || '');
  const [scheduledEndTime, setScheduledEndTime] = useState<string | undefined>(propScheduledEndTime);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [showCountdown, setShowCountdown] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(!propMessage);
  const [maintenanceActive, setMaintenanceActive] = useState<boolean>(false);

  // Fetch maintenance status if not provided as props
  useEffect(() => {
    const fetchMaintenanceStatus = async () => {
      try {
        setLoading(true);
        
        // Use the public maintenance status endpoint which checks both old and new systems
        const status = await maintenanceModeService.getStatus();
        
        // Check if maintenance is active and not expired
        let isActive = false;
        if (status.is_active && status.scheduled_end_time) {
          const endTime = new Date(status.scheduled_end_time).getTime();
          const now = new Date().getTime();
          isActive = now < endTime;
        } else {
          isActive = status.is_active || false;
        }
        
        setMaintenanceActive(isActive);
        
        if (propMessage && propScheduledEndTime) {
          // Props provided, use them and assume active
          setMaintenanceActive(true);
          return;
        }
        
        if (isActive) {
          setMessage(status.message || t('maintenance.defaultMessage') || 'The system is currently under maintenance. Please check back later.');
          setScheduledEndTime(status.scheduled_end_time);
        }
      } catch (error) {
        console.error('Error fetching maintenance status:', error);
        // On error, assume not in maintenance and redirect
        setMaintenanceActive(false);
      } finally {
        setLoading(false);
      }
    };

    fetchMaintenanceStatus();
  }, [propMessage, propScheduledEndTime, t]);

  const defaultMessage = t('maintenance.defaultMessage') || 'The system is currently under maintenance. Please check back later.';
  const displayMessage = message || defaultMessage;

  useEffect(() => {
    if (!scheduledEndTime) {
      setShowCountdown(false);
      return;
    }

    const updateCountdown = () => {
      const now = new Date().getTime();
      const endTime = new Date(scheduledEndTime).getTime();
      const difference = endTime - now;

      if (difference <= 0) {
        setTimeRemaining('');
        setShowCountdown(false);
        return;
      }

      const hours = Math.floor(difference / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
      } else if (minutes > 0) {
        setTimeRemaining(`${minutes}m ${seconds}s`);
      } else {
        setTimeRemaining(`${seconds}s`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [scheduledEndTime]);

  const formatEndTime = (endTime: string): string => {
    try {
      const date = new Date(endTime);
      return date.toLocaleString();
    } catch {
      return endTime;
    }
  };

  // If not loading and maintenance is not active, redirect to homepage
  if (!loading && !maintenanceActive) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-surface-bg)] flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--color-primary-bg)] mx-auto"></div>
          <p className="mt-4 text-[var(--color-secondary-text)]">{t('common.loading') || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-primary-bg)] flex flex-col p-4">
      {/* T-MOS Logo Header */}
      <div className="bg-[var(--color-primary-bg)] py-4 mb-8">
        <div className="flex justify-center items-center">
          <img
            src={tmosLogo}
            alt="T-MOS logo"
            className="h-10 w-auto"
          />
        </div>
      </div>

      {/* Maintenance Content */}
      <div className="flex-1 flex items-center justify-center">
        <Card variant="elevated" padding="lg" className="max-w-2xl w-full">
          <CardContent className="text-center">
            <div className="mb-6 flex justify-center">
              <WrenchScrewdriverIcon className="w-24 h-24 text-[var(--color-primary-text)]" />
            </div>
            
            <h1 className="text-3xl font-bold text-[var(--color-primary-text)] mb-4">
              {t('maintenance.screenTitle') || 'System Maintenance'}
            </h1>
            
            <p className="text-lg text-[var(--color-primary-text)] mb-6 whitespace-pre-line">
              {displayMessage}
            </p>
            
            {scheduledEndTime && (
              <div className="mt-6 p-4 bg-[var(--color-secondary-bg)] rounded-lg">
                {showCountdown && timeRemaining ? (
                  <div>
                    <p className="text-sm text-[var(--color-secondary-text)] mb-2">
                      {t('maintenance.estimatedTimeRemaining') || 'Estimated time remaining:'}
                    </p>
                    <p className="text-2xl font-bold text-[var(--color-primary-bg)]">
                      {timeRemaining}
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-[var(--color-secondary-text)] mb-2">
                      {t('maintenance.scheduledEndLabel') || 'Maintenance scheduled to end:'}
                    </p>
                    <p className="text-lg font-semibold text-[var(--color-primary-bg)]">
                      {formatEndTime(scheduledEndTime)}
                    </p>
                  </div>
                )}
              </div>
            )}
            
            <p className="text-sm text-[var(--color-primary-text)] mt-6">
              {t('maintenance.apologyMessage') || 'We apologize for any inconvenience. Thank you for your patience.'}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

